// The public screen a support link leads to (INC-13, CA-PLT-05): outside every
// session, branded by bootstrap from the host like the access screen. The
// secret is read from the fragment of the address, which no browser sends to
// any server, and taken off it at once; the link is exchanged as the screen
// opens, and the office opens on its first section, to read. A link refused
// says why; nothing on the device is touched by the refusal.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CODES, isApiError, isNetworkError } from '../api/errors';
import { brandOf } from '../app/brand';
import { useRuntime, useSession } from '../app/hooks';
import { homeOf } from '../app/sections';
import { BrandLockup } from '../ui/BrandLockup';

type Outcome = 'entering' | 'refused' | 'expired' | 'suspended' | 'failed';

function secretFromFragment(hash: string): string {
  return decodeURIComponent(hash.replace(/^#/, '')).trim();
}

function outcomeOf(failure: unknown): Outcome {
  if (isNetworkError(failure) || !isApiError(failure)) return 'failed';
  if (failure.code === CODES.supportAccessExpired) return 'expired';
  if (failure.code === CODES.organizationSuspended) return 'suspended';
  if (failure.code === CODES.supportAccessUnknown || failure.code === CODES.supportAccessConsumed)
    return 'refused';
  return 'failed';
}

export function SupportScreen() {
  const { t } = useTranslation();
  const { session } = useRuntime();
  const snapshot = useSession();
  const navigate = useNavigate();
  const [secret] = useState(() => secretFromFragment(window.location.hash));
  const [outcome, setOutcome] = useState<Outcome>(secret === '' ? 'refused' : 'entering');
  const tried = useRef(false);
  const organization = snapshot.bootstrap?.organization ?? null;
  const brand = brandOf(snapshot);

  useEffect(() => {
    // One exchange, whatever renders twice: the link serves once.
    if (tried.current || secret === '') return;
    tried.current = true;
    window.history.replaceState(null, '', window.location.pathname);
    session
      .enterSupport(secret)
      .then(() => {
        const profile = session.getSnapshot().profile;
        void navigate(homeOf(profile?.role ?? 'admin', profile?.modules ?? []), {
          replace: true,
        });
      })
      .catch((failure: unknown) => {
        setOutcome(outcomeOf(failure));
      });
  }, [secret, session, navigate]);

  return (
    <div className="access" data-testid="support-screen" data-outcome={outcome}>
      <BrandLockup
        name={organization !== null ? organization.name : t('access.neutralTitle')}
        logoUrl={brand.logoUrl}
        markUrl={brand.markUrl}
      />
      <div className="card stack">
        {outcome === 'entering' ? (
          <p className="muted" role="status">
            {t('support.entering')}
          </p>
        ) : (
          <div className="alert warn" role="alert" data-testid="support-refused">
            {t(`support.outcome.${outcome}`)}
          </div>
        )}
      </div>
    </div>
  );
}
